import axios from 'axios'
import Cookies from 'js-cookie'

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8100'

class AuthService {
  async login(email: string, password: string) {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    })

    const { access_token, refresh_token, user } = response.data

    // Store tokens
    Cookies.set('access_token', access_token, { expires: 1/48 }) // 30 minutes
    Cookies.set('refresh_token', refresh_token, { expires: 7 })

    // Set default header
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

    return { user, access_token }
  }

  async logout() {
    Cookies.remove('access_token')
    Cookies.remove('refresh_token')
    delete axios.defaults.headers.common['Authorization']
  }

  async refreshToken() {
    const refresh_token = Cookies.get('refresh_token')
    if (!refresh_token) throw new Error('No refresh token')

    const response = await axios.post(`${API_URL}/api/auth/refresh`, {
      refresh_token
    })

    const { access_token } = response.data
    Cookies.set('access_token', access_token, { expires: 1/48 })
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

    return access_token
  }

  getToken() {
    return Cookies.get('access_token')
  }

  isAuthenticated() {
    return !!this.getToken()
  }

  setupAxiosInterceptors() {
    // Request interceptor to add token
    axios.interceptors.request.use(
      (config) => {
        const token = this.getToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor to handle 401 errors
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            await this.refreshToken()
            return axios(originalRequest)
          } catch (refreshError) {
            this.logout()
            window.location.href = '/login'
            return Promise.reject(refreshError)
          }
        }

        return Promise.reject(error)
      }
    )
  }
}

const authService = new AuthService()
authService.setupAxiosInterceptors()

export default authService